import {FC, useEffect, useState} from "react";
import "./styles.scss";
import { Button, Dropdown, QuestionTooltip } from "../../components";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowRight32 } from "@carbon/icons-react";
import {
  tooltipMessages,
} from "../index";
import { getDropdownOptions } from "../../utilities/api";
import { getOptionData } from "../../utilities/helpers";
import useWindowSize, {
  deviceWidthBreakpoints
} from "../../hooks/useWindowSize";

const { MOBILE_MAX, TABLET_MAX } = deviceWidthBreakpoints;

interface LandingProps {
  goNextSection: Function;
  formData: Object;
}

interface stateType {
  fromReview: boolean;
}

const Landing: FC<LandingProps> = ({ goNextSection, formData }) => {
  const { width } = useWindowSize();

  const languagePlaceholder = "Select";
  const affiliatePlaceholder = "Select Affiliate";

  const [nextProgressIndex, setNextProgressIndex] = useState(0);
  const [nextSection, setNextSection] = useState("/issue");

  const affiliateOptions = window.apiConfig.regionalConfigs.map(
      (regionalConfig) => {
        return {
          label: regionalConfig["affiliateCountry"],
          value: regionalConfig["affiliateCode"],
        };
      }
  );

  const schema = yup.object().shape({
    language: yup.string().required("Required").notOneOf([languagePlaceholder], "Required"),
    affiliate: yup.string().required("Required").notOneOf([affiliatePlaceholder], "Required")
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    setValue,
    getValues
  } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });

  let history = useHistory();

  const onSubmit = (data) => {
    getDropdownOptions(data.affiliate);
    goNextSection(data, nextProgressIndex);
    history.push(nextSection);
  };

  const { state: fromReview } = useLocation<stateType>();

  const onBackToReview = () => {
    setNextProgressIndex(6);
    setNextSection("/review");
  };

  const affiliateSelectionTooltip = (
    <QuestionTooltip message={tooltipMessages.affiliateSelection} />
  );

  const languageOptions = [{ value: "en", label: "English" }];

  useEffect(() => {
    setValue("languageLabel", languageOptions[0].label);
  }, []);

  return (
    <div className="tab-container">
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className="tab-title">
          Welcome to Shared AbbVie Reporting Platform (ShARP)
        </div>
        <br />
        <div className="page-notification">
          <p>Use this tool to report:</p>
          <ul>
            <li>
            Adverse Events resulting from administration of a medical drug product
            </li>
            <li>

              Product Quality Complaints for L-ABBV & L-AGN Pharma products

            </li>
          </ul>
          <p>
            Do NOT report any stand-alone medical device complaints (e.g.: Body Sculpting, Breast Implants, etc.) through ShARP. 
            Please continue following regular process.
          </p>
          <br />

          <p>
            This should take approximately 5 minutes to fill out - please note
            if you navigate away or refresh, your data will be cleared.
          </p>
          <br />
          <p>
            There is a time limit to submitting reports via ShARP. Please submit your report within 10 minutes otherwise your data will be lost.
          </p>

        </div>
        <div className="form-sections-container">
          <div className={`form-section ${width > MOBILE_MAX && "form-section-horizontal"}`}>
            <Dropdown
              control={control}
              Controller={Controller}
              setValue={setValue}
              getValues={getValues}
              name="language"
              label="Language *"
              items={languageOptions}
              defaultItem={
                getOptionData(languageOptions, formData["language"]) ||
                languageOptions[0]
              }
              placeholder={languagePlaceholder}
              size={width > MOBILE_MAX ? "medium" : "extra-extra-large"}
              error={errors.language?.message}
            />
            <Dropdown
              control={control}
              Controller={Controller}
              setValue={setValue}
              getValues={getValues}
              name="affiliate"
              label="To which Affiliate do you report? *"
              items={affiliateOptions}
              defaultItem={getOptionData(
                affiliateOptions,
                formData["affiliate"]
              )}
              placeholder={affiliatePlaceholder}
              error={errors.affiliate?.message}
              QuestionTooltip={affiliateSelectionTooltip}
              size={width > MOBILE_MAX ? "large" : "extra-extra-large"}
            />
          </div>
        </div>
        <div className="form-button-container begin-entry-button">
          {!fromReview && (
            <Button
              type="submit"
              label={"Begin Entry"}
              disabled={!isValid}
              renderIcon={ArrowRight32}
            />
          )}
          {fromReview && (
            <div className="back-to-review-button">
              <Button
                label="Back to Review"
                disabled={!isValid}
                kind="primary"
                type="submit"
                onClick={onBackToReview}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default Landing;